import { useId, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRight, Trash, TriangleAlert, Video } from "lucide-react";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  DatePicker,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
  Alert,
  AlertDescription,
  MoneyInput,
  MultiSelect,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui";
import {
  useAuth,
  useFetchSettingsTenantQuery,
  useUpdateSchedulingMutation,
  useFetchEmployeesNoPaginateQuery,
  useFetchPatientsNoPaginateQuery,
  useToast,
  useConfirmSchedulingMutation,
  useFetchProceduresQuery,
  useFetchFinanceAccountsQuery,
  useFetchFinanceCategoriesQuery,
  useFinishedSchedulingMutation,
  useCancelSchedulingMutation,
  usePermission,
} from "@/hooks";
import {
  defaultEntriesCategories,
  defaultFinancialAccounts,
  hasFeature,
} from "@/helpers";
import {
  DisplayError,
  formatDateForDatePicker,
  formatDateToBackend,
} from "@/utils";
import {
  FinishedRequestDTO,
  Scheduling,
  UpdateScheduleRequestDTO,
} from "@/types";
import {
  SchedulingFinishedValidationSchema,
  UpdateSchedulingsValidationSchema,
} from "@/shared";

import { AlertDialogDelete } from "../../dialogs";
import { MessageError } from "../../message-error";
import { AppointmentHistory } from "./appointment-history";
import { SettlementStatusFields } from "./settlement-status-fields";

type FormEditSchedulingProps = {
  scheduling: Scheduling;
  handleClose: () => void;
  patientId?: string;
};

export function FormEditScheduling({
  scheduling,
  handleClose,
  patientId
}: FormEditSchedulingProps) {
  const { t } = useTranslation();

  const { checkPermission } = usePermission();

  const { toast } = useToast();

  const { user } = useAuth();
  const {
    data: clinicSettings, isLoading: settingsLoading, isError: settingsError,
    identityReady, refetch: refetchSettings,
  } = useFetchSettingsTenantQuery();
  let clinicToday: string | undefined;
  if (identityReady && !settingsLoading && !settingsError && clinicSettings?.timezone) {
    try {
      clinicToday = new Intl.DateTimeFormat("en-CA", {
        timeZone: clinicSettings.timezone,
        year: "numeric", month: "2-digit", day: "2-digit",
      }).format(new Date());
    } catch {
      // Invalid clinic settings must not silently select the browser timezone.
    }
  }
  const isFutureAppointment = clinicToday !== undefined && formatDateToBackend(scheduling.date) > clinicToday;
  // SCHED-TZ-01: a read that succeeded without a usable timezone is a clinic
  // setup gap, not a failure to retry. It is explained beside the control it
  // blocks, and only when nothing else already blocks that control.
  const timezoneMissing = identityReady && !settingsLoading && !settingsError && Boolean(clinicSettings) && !clinicToday;
  const canRecordNoShow =
    checkPermission("scheduling", "canceled scheduling") &&
    checkPermission("scheduling", "finished scheduling") &&
    ["pending", "confirmed"].includes(scheduling.status);
  const noShowTimezoneReasonId = useId();
  const showNoShowTimezoneReason = timezoneMissing && canRecordNoShow;
  const isAdmin = user?.roles?.some((role) => role.name === "admin") ?? false;
  const googleCalendarIncluded = hasFeature(user, "google_calendar");

  const [generatePayment, setGeneratePayment] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [closingOutcome, setClosingOutcome] = useState<"canceled" | "no_show">("canceled");
  const [cancellationReason, setCancellationReason] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(formatDateToBackend(scheduling.date));
  const [rescheduleHour, setRescheduleHour] = useState(scheduling.hour.slice(0, 5));
  const [rescheduleEmployee, setRescheduleEmployee] = useState(scheduling.employee.id);
  const [showFinalizeConfirmModal, setShowFinalizeConfirmModal] =
    useState(false);

  const { data: employeesData, isLoading: employeesLoading } =
    useFetchEmployeesNoPaginateQuery();
  const { data: patientsData, isLoading: patientsLoading } =
    useFetchPatientsNoPaginateQuery();
  const { data: proceduresData, isLoading: proceduresLoading } =
    useFetchProceduresQuery({ perPage: "100" });
  const { mutateAsync, isLoading } = useUpdateSchedulingMutation();
  const {
    mutateAsync: mutateAsyncFinishScheduling,
    isLoading: isLoadingFinishScheduling,
  } = useFinishedSchedulingMutation();
  const {
    mutateAsync: mutateAsyncCancelScheduling,
    isLoading: isLoadingCancelScheduling,
  } = useCancelSchedulingMutation();

  const { data: dataAccounts, isLoading: isLoadingAccounts } =
    useFetchFinanceAccountsQuery({ per_page: 100 });
  const { data: dataCategories, isLoading: isLoadingCategories } =
    useFetchFinanceCategoriesQuery({});

  const {
    mutateAsync: mutateAsyncConfirm,
    isLoading: isLoadingConfirmSchelule,
  } = useConfirmSchedulingMutation();

  const initialValues = {
    expected_revision: scheduling.revision ?? 0,
    id: scheduling?.id || "",
    patientsId: patientId || scheduling?.patient.id || "",
    employeesId: scheduling?.employee.id || "",
    proceduresId: scheduling?.procedure.id || "",
    date: formatDateForDatePicker(scheduling?.date) || "",
    value: scheduling?.value || "",
    hour: scheduling?.hour || "",
    return: Number(scheduling?.return) || 0,
    comments: scheduling?.comments || "",
    accountId: "",
    categoryId: "",
    onlyFinished: false,
    settlement_status: "paid" as "paid" | "receivable",
    due_date: "",
    confirmWhatsApp: scheduling?.confirmWhatsApp || false,
    mode: "unique",
    type_periodic: "",
    total_installments: "",
    entry: "",
    payment_method: "",
    syncWithGoogle:
      user?.notificationsSettings?.google_calendar[
      "Automatic synchronization"
      ] || false,
  };

  const formik = useFormik({
    initialValues,
    validationSchema:
      scheduling?.status === "confirmed" && generatePayment
        ? SchedulingFinishedValidationSchema()
        : UpdateSchedulingsValidationSchema(),
    onSubmit: async (scheduling) => {
      handleUpdateScheduleSubmit(scheduling);
    },
  });

  async function handleUpdateScheduleSubmit(
    schedulingData: UpdateScheduleRequestDTO | FinishedRequestDTO
  ) {
    try {
      const request = schedulingData;
      if (schedulingData.date) {
        request.date = formatDateToBackend(schedulingData.date);
      }
      await mutateAsync(request as UpdateScheduleRequestDTO, {
        onSuccess: () => {
          handleClose();
          toast({
            title: "Sucesso!",
            description: "Agendamento atualizado com sucesso",
            variant: "default",
          });
        },
      });
      return;
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    } finally {
      formik.setSubmitting(false);
    }
  }

  async function handleFinalizeSchedulings(schedulingData: FinishedRequestDTO) {
    try {
      formik.setFieldTouched("value", true);
      formik.setFieldTouched("accountId", true);
      formik.setFieldTouched("categoryId", true);
      formik.setFieldTouched("payment_method", true);
      formik.setFieldTouched("settlement_status", true);
      formik.setFieldTouched("due_date", true);
      formik.setFieldTouched("mode", true);
      formik.setFieldTouched("type_periodic", true);
      formik.setFieldTouched("total_installments", true);
      formik.setFieldTouched("entry", true);

      const errors = await formik.validateForm();

      const isValid = Object.keys(errors).length === 0;

      const request = schedulingData;
      if (
        isValid &&
        scheduling.status === "confirmed" &&
        formik.values.value !== ""
      ) {
        await mutateAsyncFinishScheduling(request as FinishedRequestDTO, {
          onSuccess: () => {
            handleClose();
            toast({
              title: "Sucesso!",
              description: "Agendamento Finalizado com sucesso",
              variant: "default",
            });
          },
        });
      }

    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    } finally {
      formik.setSubmitting(false);
    }
  }

  async function confirmScheduling() {
    try {
      await mutateAsyncConfirm({ id: scheduling.id, expected_revision: scheduling.revision ?? 0 });
      handleClose();
      toast({ title: "Agendamento confirmado", description: "O horário reservado foi mantido." });
    } catch (error) {
      toast({
        title: "Não foi possível confirmar",
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  async function cancelScheduling() {
    try {
      await mutateAsyncCancelScheduling({
        id: scheduling.id, outcome: closingOutcome, reason: cancellationReason.trim(), expected_revision: scheduling.revision ?? 0,
      });
      setShowCancelConfirmation(false);
      handleClose();
      toast({ title: closingOutcome === "no_show" ? "Falta registrada" : "Agendamento cancelado", description: "O horário ficou disponível na agenda." });
    } catch (error) {
      toast({ title: "Não foi possível cancelar", description: DisplayError(error), variant: "destructive" });
    }
  }

  async function handleFinalizeQuickly(scheduling: Scheduling) {
    try {
      formik.setFieldTouched("value", true);
      if (formik.values.value !== "") {
        await mutateAsyncFinishScheduling(
          {
            id: scheduling.id,
            expected_revision: scheduling.revision ?? 0,
            onlyFinished: true,
            value: scheduling.value,
            mode: "unique",
          },
          {
            onSuccess: () => {
              handleClose();
              toast({
                title: t("LABEL.success"),
                description: t("Agendamento finalizado com sucesso."),
                variant: "default",
              });
            },
          }
        );
      }
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error")),
        variant: "destructive",
      });
    }
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="max-2xl:h-[94vh] max-sm:h-[90vh] max-2xl:overflow-y-auto"
    >
      <div className="h-full overflow-y-scroll flex flex-col">
        <DrawerHeader className="flex-none">
          <DrawerTitle className="font-display text-app-fg-strong">
            Editar agendamento
          </DrawerTitle>
          <DrawerDescription>
            Edite os dados ou confirme o horário reservado. Confirmar não altera os dados do agendamento.
          </DrawerDescription>
          {(!identityReady || settingsLoading) ? (
            <p role="status">Carregando o fuso horário da clínica para verificar a data do atendimento…</p>
          ) : settingsError ? (
            <div role="alert">
              Não foi possível carregar o fuso horário da clínica.
              <Button type="button" variant="outline" onClick={() => void refetchSettings()}>Tentar novamente</Button>
            </div>
          ) : null}
          {isFutureAppointment && (
            <p role="note" className="mt-3 rounded-xl bg-app-warning/10 p-3 text-sm text-app-fg-strong">
              Este atendimento está marcado para uma data futura. Finalize somente após a realização.
              Um pagamento antecipado deve ser registrado no Financeiro, sem finalizar o atendimento.
            </p>
          )}
          {scheduling?.hour && (
            <div className="mt-3 flex items-center justify-center">
              <div className="bg-brand-soft border-2 border-brand rounded-lg px-6 py-2 inline-flex items-center gap-2">
                <div className="size-4 rounded-full bg-brand flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <span className="text-app-fg-strong font-medium text-sm">
                  Horário:{" "}
                  <span className="font-bold tabular-nums text-brand">
                    {scheduling.hour} - {scheduling.endtime}
                  </span>
                </span>
              </div>
              {scheduling.telehealth_url && (
                <a
                  href={scheduling.telehealth_url}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-3 bg-app-surface border-2 border-petrol rounded-lg px-4 py-2 inline-flex items-center gap-2 hover:bg-app-surface-muted transition-colors"
                >
                  <Video className="size-4 text-petrol" aria-hidden="true" />
                  <span className="text-petrol font-medium text-sm">
                    Acessar Telemedicina
                  </span>
                </a>
              )}
            </div>
          )}
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-10">
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-5">
              <div className="flex flex-col gap-2">
                <Label required>{t("LABEL.patient")}</Label>
                <div className="space-y-1">
                  <MultiSelect
                    options={
                      patientsData?.map((patient) => ({
                        value: patient.id,
                        label: patient.person.name,
                      })) || []
                    }
                    loading={patientsLoading}
                    placeholder="Selecione um paciente..."
                    width="300px"
                    isInvalid={
                      formik.touched.patientsId && formik.errors.patientsId
                    }
                    isValid={
                      formik.touched.patientsId && !formik.errors.patientsId
                    }
                    value={formik.values.patientsId}
                    onChange={(value) =>
                      formik.setFieldValue("patientsId", value)
                    }
                    onBlur={() => formik.handleBlur("patientsId")}
                    messageError={formik.errors.patientsId}
                    disabled
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label required>{t("LABEL.collaborator")}</Label>
                <div className="space-y-1">
                  <MultiSelect
                    options={
                      employeesData?.map((employee) => ({
                        value: employee.id,
                        label: employee.person.name,
                      })) || []
                    }
                    loading={employeesLoading}
                    placeholder="Selecione um colaborador..."
                    width="300px"
                    isInvalid={
                      formik.touched.employeesId && formik.errors.employeesId
                    }
                    isValid={
                      formik.touched.employeesId && !formik.errors.employeesId
                    }
                    value={formik.values.employeesId}
                    onChange={(value) =>
                      formik.setFieldValue("employeesId", value)
                    }
                    onBlur={() => formik.handleBlur("employeesId")}
                    messageError={formik.errors.employeesId}
                    disabled
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label required>{t("LABEL.procedure")}</Label>
                <div className="space-y-1">
                  <MultiSelect
                    options={
                      proceduresData?.items.map((procedure) => ({
                        value: procedure.id,
                        label: procedure.name,
                      })) || []
                    }
                    loading={proceduresLoading}
                    isInvalid={
                      formik.touched.proceduresId && formik.errors.proceduresId
                    }
                    isValid={
                      formik.touched.proceduresId && !formik.errors.proceduresId
                    }
                    placeholder="Selecione um procedimento..."
                    emptyMessage="Nenhum procedimento encontrado"
                    width="300px"
                    value={formik.values.proceduresId}
                    onChange={(value) =>
                      formik.setFieldValue("proceduresId", value)
                    }
                    onBlur={() => formik.handleBlur("proceduresId")}
                    messageError={formik.errors.proceduresId}
                    disabled
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label required>{t("LABEL.date")}</Label>
                <DatePicker
                  {...formik.getFieldProps("date")}
                  isInvalid={formik.touched.date && !!formik.errors.date}
                  isValid={formik.touched.date && !formik.errors.date}
                  messageError={formik.errors.date}
                  disabled
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label required>{t("LABEL.value")}</Label>
                <MoneyInput
                  placeholder={t("LABEL.value")}
                  defaultValue={formik.values.value}
                  isInvalid={formik.touched.value && formik.errors.value}
                  isValid={formik.touched.value && !formik.errors.value}
                  {...formik.getFieldProps("value")}
                  messageError={formik.errors.value}
                  disabled={scheduling.status === "finished"}
                />
              </div>
            </div>
          </div>

          {scheduling?.status === "confirmed" && (
            <div>
              <div className="w-full mt-5 pb-5">
                <div className="flex items-center justify-center space-x-2">
                  <Switch
                    id="generate-payment"
                    checked={generatePayment}
                    onCheckedChange={(checked) => {
                      setGeneratePayment(checked);
                    }}
                    disabled={scheduling?.status !== "confirmed"}
                  />
                  <Label htmlFor="generate-payment">Gerar pagamento</Label>
                </div>
              </div>

              <div className="border border-app-border rounded-lg p-3 mb-5">
                <div className="w-full py-5 flex items-center gap-1">
                  <ChevronRight size={20} className="text-brand" aria-hidden="true" />
                  <h2 className="font-display text-xl text-app-fg-strong mt-1">
                    {t("LABEL.payment")}{" "}
                    <span className="font-normal text-app-fg-muted text-base">
                      (Opcional)
                    </span>
                  </h2>
                </div>
                <div className="mb-5">
                  <SettlementStatusFields
                    idPrefix="edit-settlement"
                    status={formik.values.settlement_status}
                    dueDate={formik.values.due_date}
                    disabled={!generatePayment}
                    statusError={formik.errors.settlement_status}
                    dueDateError={formik.errors.due_date}
                    dueDateTouched={formik.touched.due_date}
                    onStatusChange={(value) => {
                      formik.setFieldValue("settlement_status", value);
                      if (value === "paid") {
                        formik.setFieldValue("due_date", "");
                      }
                    }}
                    onDueDateChange={formik.getFieldProps("due_date").onChange}
                    onDueDateBlur={formik.getFieldProps("due_date").onBlur}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 pb-3">
                  <>
                    <div className="space-y-1">
                      <Label required>{t("LABEL.account")}</Label>

                      <div className="space-y-1">
                        <Select
                          {...formik.getFieldProps("accountId")}
                          onValueChange={(value) => {
                            formik.setFieldValue("accountId", value);
                          }}
                          disabled={scheduling?.status !== "confirmed"}
                        >
                          <SelectTrigger
                            isInvalid={
                              formik.touched.accountId &&
                              formik.errors.accountId
                            }
                            isValid={
                              formik.touched.accountId &&
                              !formik.errors.accountId
                            }
                            loading={isLoadingAccounts || !dataAccounts}
                            disabled={!generatePayment}
                          >
                            <SelectValue placeholder="Selecione a conta" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {dataAccounts &&
                                dataAccounts.items.map((account) => (
                                  <SelectItem
                                    key={account.id}
                                    value={account.id}
                                  >
                                    {defaultFinancialAccounts.includes(
                                      account.name
                                    )
                                      ? t(account.name.toLowerCase())
                                      : account.name}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <MessageError
                          isInvalid={
                            formik.touched.accountId && formik.errors.accountId
                          }
                          messageError={formik.errors.accountId}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label required>{t("LABEL.category")}</Label>

                      <MultiSelect
                        options={
                          dataCategories?.map((category) => ({
                            value: category.id,
                            label: defaultEntriesCategories.includes(category.name.toLowerCase())
                              ? <span className="capitalize">{t(`LABEL.${category.name.toLowerCase()}`)}</span>
                              : <span className="capitalize">{category.name}</span>,
                          })) || []
                        }
                        loading={isLoadingCategories || !dataCategories}
                        placeholder="Selecione a categoria..."
                        isInvalid={
                          formik.touched.categoryId && formik.errors.categoryId
                        }
                        isValid={
                          formik.touched.categoryId && !formik.errors.categoryId
                        }
                        value={formik.values.categoryId}
                        onChange={(value) => {
                          formik.setFieldValue("categoryId", value);
                        }}
                        onBlur={() => formik.handleBlur("categoryId")}
                        messageError={formik.errors.categoryId}
                        disabled={
                          scheduling?.status !== "confirmed" || !generatePayment
                        }
                      />
                    </div>
                    <div className="space-y-1">
                      <Label required>{t("LABEL.payment method")}</Label>

                      <div className="space-y-1">
                        <Select
                          {...formik.getFieldProps("payment_method")}
                          onValueChange={(value) => {
                            formik.setFieldValue("payment_method", value);
                          }}
                          disabled={scheduling?.status !== "confirmed"}
                        >
                          <SelectTrigger
                            isInvalid={
                              formik.touched.payment_method &&
                              formik.errors.payment_method
                            }
                            isValid={
                              formik.touched.payment_method &&
                              !formik.errors.payment_method
                            }
                            disabled={!generatePayment}
                          >
                            <SelectValue placeholder="Selecione a forma de pagamento" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="Pix">Pix</SelectItem>
                              <SelectItem value="Money">
                                {t("LABEL.money")}
                              </SelectItem>
                              <SelectItem value="Ticket">Boleto</SelectItem>
                              <SelectItem value="AutomaticDebit">
                                {t("LABEL.automatic debit")}
                              </SelectItem>
                              <SelectItem value="BankDeposit">
                                {t("LABEL.bank deposit")}
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <MessageError
                          isInvalid={
                            formik.touched.payment_method &&
                            formik.errors.payment_method
                          }
                          messageError={formik.errors.payment_method}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label required>
                        {t("LABEL.installments or repeat installments")}
                      </Label>
                      <div className="space-y-1">
                        <Select
                          {...formik.getFieldProps("mode")}
                          onValueChange={(value) => {
                            formik.setFieldValue("mode", value);
                          }}
                          defaultValue="unique"
                          disabled={scheduling?.status !== "confirmed"}
                        >
                          <SelectTrigger
                            isInvalid={
                              formik.touched.mode && formik.errors.mode
                            }
                            isValid={formik.touched.mode && !formik.errors.mode}
                            disabled={!generatePayment}
                          >
                            <SelectValue placeholder="Selecione uma das opções" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectItem value="unique">
                                Não parcelar
                              </SelectItem>
                              <SelectItem value="installment">
                                Sim, parcelar
                              </SelectItem>
                              <SelectItem value="periodic">
                                Sim, repetir periodicamente
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <MessageError
                          isInvalid={formik.touched.mode && formik.errors.mode}
                          messageError={formik.errors.mode}
                        />
                      </div>
                    </div>

                    {(formik.values.mode === "periodic" ||
                      formik.values.mode === "installment" ||
                      formik.values.mode === "periodic" ||
                      formik.values.mode === "installment") && (
                        <>
                          <div className="space-y-1">
                            <Label>{t("LABEL.period")}</Label>
                            <div className="space-y-1">
                              <Select
                                {...(scheduling.status === "confirmed"
                                  ? formik.getFieldProps("type_periodic")
                                  : formik.getFieldProps("type_periodic"))}
                                onValueChange={(value) => {
                                  formik.setFieldValue("type_periodic", value);
                                }}
                                disabled={scheduling?.status !== "confirmed"}
                              >
                                <SelectTrigger
                                  isInvalid={
                                    formik.touched.type_periodic &&
                                    formik.errors.type_periodic
                                  }
                                  isValid={
                                    formik.touched.type_periodic &&
                                    !formik.errors.type_periodic
                                  }
                                >
                                  <SelectValue placeholder="Selecione a categoria" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    <SelectItem value="daily">Diário</SelectItem>
                                    <SelectItem value="weekly">
                                      Semanal
                                    </SelectItem>
                                    <SelectItem value="biweekly">
                                      Bi-semanal
                                    </SelectItem>
                                    <SelectItem value="monthly">
                                      Mensal
                                    </SelectItem>
                                    <SelectItem value="annually">
                                      Anual
                                    </SelectItem>
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                              <MessageError
                                isInvalid={
                                  formik.touched.type_periodic &&
                                  formik.errors.type_periodic
                                }
                                messageError={formik.errors.type_periodic}
                              />
                            </div>
                          </div>
                        </>
                      )}

                    {formik.values.mode === "installment" && (
                      <>
                        <div className="space-y-1">
                          <Label>{t("LABEL.installments")}</Label>
                          <div className="space-y-1">
                            <Select
                              {...(scheduling.status === "confirmed"
                                ? formik.getFieldProps("total_installments")
                                : formik.getFieldProps("total_installments"))}
                              onValueChange={(value) => {
                                formik.setFieldValue(
                                  "total_installments",
                                  value
                                );
                              }}
                              disabled={scheduling?.status !== "confirmed"}
                            >
                              <SelectTrigger
                                isInvalid={
                                  formik.touched.total_installments &&
                                  formik.errors.total_installments
                                }
                                isValid={
                                  formik.touched.total_installments &&
                                  !formik.errors.total_installments
                                }
                              >
                                <SelectValue placeholder="Selecione o total de parcelas" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  {Array.from({ length: 96 }, (_, index) => (
                                    <SelectItem
                                      key={index}
                                      value={String(index + 1)}
                                    >
                                      {index + 1} Parcelas
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                            <MessageError
                              isInvalid={
                                formik.touched.total_installments &&
                                formik.errors.total_installments
                              }
                              messageError={formik.errors.total_installments}
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label>{t("LABEL.entry value")}</Label>
                          <Input
                            type="number"
                            placeholder="R$ 0,00"
                            isInvalid={
                              formik.touched.entry && formik.errors.entry
                            }
                            isValid={
                              formik.touched.entry && !formik.errors.entry
                            }
                            {...formik.getFieldProps("entry")}
                            messageError={formik.errors.entry}
                            disabled={scheduling?.status !== "confirmed"}
                          />
                        </div>
                      </>
                    )}
                  </>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>{t("LABEL.description")}</Label>
                <Textarea
                  placeholder="Descrição"
                  {...formik.getFieldProps("comments")}
                  isInvalid={formik.touched.comments && formik.errors.comments}
                  isValid={formik.touched.comments && !formik.errors.comments}
                />
              </div>
            </div>
          )}
          {/* confirm_whatsapp */}
          <div className="flex justify-center items-center gap-5">
            <div className="flex items-center space-x-2">
              <Switch
                id="return"
                checked={formik.values.return ? true : false}
                onCheckedChange={(checked) =>
                  formik.setFieldValue("return", checked ? 1 : 0)
                }
                disabled={scheduling.status === "finished"}
              />
              <Label htmlFor="return">Retorno</Label>
            </div>

            {/* SCRUM-140: Google Agenda is a plan feature, on this form too. */}
            {googleCalendarIncluded ? (
              <div className="flex items-center space-x-2">
                <Switch
                  id="sync-google"
                  checked={formik.values.syncWithGoogle ? true : false}
                  onCheckedChange={(checked) =>
                    formik.setFieldValue("syncWithGoogle", checked ? 1 : 0)
                  }
                  disabled={scheduling.status === "finished"}
                />
                <Label htmlFor="sync-google">Sincronizar com Google</Label>
              </div>
            ) : null}
            <div className="flex items-center space-x-2">
              <Switch
                id="confirm_whatsapp"
                checked={formik.values.confirmWhatsApp ? true : false}
                onCheckedChange={(checked) =>
                  formik.setFieldValue("confirmWhatsApp", checked)
                }
                disabled={scheduling.status === "finished"}
              />
              <Label htmlFor="confirm_whatsapp">Confirmar pelo WhatsApp</Label>
            </div>
          </div>

          <div className="space-y-2">
            {scheduling.payment_history === 0 &&
              scheduling.status === "finished" && (
                <div className="text-center">
                  <p className="text-sm text-app-danger">
                    Este agendamento foi finalizado sem registro de pagamento
                  </p>
                </div>
              )}
            {scheduling.status === "finished" && (
              <div className="max-w-sm mx-auto">
                <Alert className="border border-app-warning/40 bg-app-warning/10 text-app-fg-strong py-1">
                  <TriangleAlert className="size-6 text-app-warning" aria-hidden="true" />
                  {/* <AlertTitle>{t("LABEL.scheduling Completed")}!</AlertTitle> */}
                  <AlertDescription className="space-y-1 text-sm text-app-fg mt-3">
                    <p className="font-semibold">
                      {t("ALERT.finalized_scheduling")}
                    </p>
                    <p>{t("ALERT.finalized_scheduling_desc")}</p>
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </div>

          <AppointmentHistory scheduling={scheduling} timezone={clinicToday ? clinicSettings?.timezone ?? undefined : undefined} />
          <div className="space-y-3">
            <Button type="button" variant="outline" disabled={!checkPermission("scheduling", "update scheduling") || !["pending", "confirmed"].includes(scheduling.status)} onClick={() => setShowReschedule(!showReschedule)}>
              Reagendar
            </Button>
            {showReschedule && <section aria-label="Reagendar atendimento" className="space-y-3 rounded-xl border p-3">
              <p>Escolha a nova data, horário e profissional. Paciente, procedimento, valor e duração serão mantidos.</p>
              <Label htmlFor="reschedule-date">Nova data</Label>
              <Input id="reschedule-date" type="date" value={rescheduleDate} onChange={(event) => setRescheduleDate(event.target.value)} />
              <Label htmlFor="reschedule-hour">Novo horário</Label>
              <Input id="reschedule-hour" type="time" value={rescheduleHour} onChange={(event) => setRescheduleHour(event.target.value)} />
              <Label htmlFor="reschedule-employee">Profissional do reagendamento</Label>
              <select id="reschedule-employee" className="w-full rounded border p-2" value={rescheduleEmployee} onChange={(event) => setRescheduleEmployee(event.target.value)}>
                <option value={scheduling.employee.id}>{scheduling.employee.name}</option>
                {employeesData?.filter((employee) => employee.id !== scheduling.employee.id).map((employee) => <option key={employee.id} value={employee.id}>{employee.person.name}</option>)}
              </select>
              <Button type="button" disabled={isLoading || !rescheduleDate || !rescheduleHour} onClick={() => void handleUpdateScheduleSubmit({ ...initialValues, patientsId: scheduling.patient.id, date: rescheduleDate, hour: rescheduleHour, employeesId: rescheduleEmployee, syncWithGoogle: false })}>
                Confirmar reagendamento
              </Button>
              <p>Um horário ocupado será recusado sem alterar a reserva atual. Nenhum pagamento será gerado.</p>
            </section>}
          </div>
          <DrawerFooter className="flex flex-row flex-wrap justify-center pb-24 gap-2">
            <AlertDialogDelete
              endpoint={`/schedulings/${scheduling.id}`}
              queryKey="schedulings"
              callBack={handleClose}
            >
              <Button
                type="button"
                className="flex items-center gap-2 bg-destructive hover:bg-destructive/85"
                disabled={
                  !checkPermission("scheduling", "delete scheduling") ||
                  !(scheduling.status === "pending")
                }
              >
                <Trash className="size-3" />
                <span>{t("LABEL.delete")}</span>
              </Button>
            </AlertDialogDelete>
            <Button
              type="button"
              variant="outline"
              className="flex items-center justify-center gap-2"
              isLoading={isLoadingCancelScheduling}
              disabled={
                !checkPermission("scheduling", "canceled scheduling") ||
                !(["pending", "confirmed"].includes(scheduling.status)) ||
                isLoadingCancelScheduling
              }
              onClick={() => { setClosingOutcome("canceled"); setCancellationReason(""); setShowCancelConfirmation(true); }}
            >
              <span>Cancelar agendamento</span>
            </Button>
            <div className="flex flex-col items-center gap-1">
              <Button type="button" variant="outline" aria-describedby={showNoShowTimezoneReason ? noShowTimezoneReasonId : undefined} disabled={!canRecordNoShow || isLoadingCancelScheduling || !clinicToday || isFutureAppointment} onClick={() => { setClosingOutcome("no_show"); setCancellationReason(""); setShowCancelConfirmation(true); }}>
                Não compareceu
              </Button>
              {showNoShowTimezoneReason && (
                <p id={noShowTimezoneReasonId} className="max-w-xs text-center text-xs text-app-fg-muted">
                  {isAdmin ? (
                    <>
                      Defina o fuso horário da clínica em Configurações › Clínica para registrar faltas.{" "}
                      <Link to="/settings/clinic" className="font-medium text-brand underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
                        Definir agora
                      </Link>
                    </>
                  ) : (
                    // Other roles cannot set it (OQ-49): say when, not what to do.
                    "Disponível depois que a clínica definir o fuso horário."
                  )}
                </p>
              )}
            </div>
            <AlertDialog open={showCancelConfirmation} onOpenChange={setShowCancelConfirmation}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{closingOutcome === "no_show" ? "Registrar falta neste atendimento?" : "Cancelar este agendamento?"}</AlertDialogTitle>
                  <AlertDialogDescription>
                    O atendimento de {scheduling.patient.name} será encerrado e o horário ficará disponível. Para mudar a data, use Reagendar. Nenhum pagamento será gerado ou quitado.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <Label htmlFor="cancellation-reason">{closingOutcome === "canceled" ? "Motivo do cancelamento (obrigatório)" : "Observação sobre a falta (opcional)"}</Label>
                <Textarea id="cancellation-reason" maxLength={1000} value={cancellationReason} onChange={(event) => setCancellationReason(event.target.value)} />
                <AlertDialogFooter>
                  <AlertDialogCancel>Manter agendamento</AlertDialogCancel>
                  <Button type="button" variant="destructive" disabled={isLoadingCancelScheduling || (closingOutcome === "canceled" && !cancellationReason.trim())} onClick={cancelScheduling}>
                    {closingOutcome === "no_show" ? "Confirmar falta" : "Confirmar cancelamento"}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              type="button"
              className="justify-center"
              onClick={confirmScheduling}
              disabled={
                !checkPermission("scheduling", "confirmed scheduling") ||
                !(scheduling.status === "pending") ||
                isLoadingConfirmSchelule
              }
              isLoading={isLoadingConfirmSchelule}
            >
              <span>{t("LABEL.confirm")}</span>
            </Button>
            {generatePayment ? (
              <Button
                type="button"
                onClick={() => handleFinalizeSchedulings(formik.values)}
                variant="petrol"
                className="px-10 justify-center"
                isLoading={isLoadingFinishScheduling}
                disabled={
                  !checkPermission("scheduling", "finished scheduling") ||
                  isLoadingFinishScheduling ||
                  scheduling.status !== "confirmed"
                }
              >
                {t("LABEL.finish")}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="petrol"
                  className="px-10 justify-center"
                  disabled={
                    !checkPermission("scheduling", "finished scheduling") ||
                    isLoadingFinishScheduling ||
                    scheduling.status !== "confirmed"
                  }
                  onClick={() => setShowFinalizeConfirmModal(true)}
                  isLoading={isLoadingFinishScheduling}
                >
                  {t("LABEL.finish")}
                </Button>

                <AlertDialog
                  open={showFinalizeConfirmModal}
                  onOpenChange={setShowFinalizeConfirmModal}
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Finalizar sem gerar pagamento?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Você está prestes a finalizar este agendamento sem gerar
                        pagamento. Deseja continuar?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        onClick={() => setShowFinalizeConfirmModal(false)}
                      >
                        Cancelar
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          setShowFinalizeConfirmModal(false);
                          handleFinalizeQuickly(scheduling);
                        }}
                      >
                        Finalizar sem pagamento
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}

            <Button
              type="submit"
              className="px-10"
              isLoading={isLoading}
              disabled={
                !checkPermission("scheduling", "update scheduling") ||
                isLoading ||
                generatePayment ||
                !(["pending", "confirmed"].includes(scheduling.status))
              }
            >
              {t("LABEL.save")}
            </Button>
          </DrawerFooter>
        </div>
      </div>
    </form>
  );
}
